import React, { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@contexts/AuthContext";
import { TourProvider } from "@contexts/TourContext";
import { MobileSidebarProvider } from "@contexts/MobileSidebarContext";
import { NavigationGuardProvider } from "@contexts/NavigationGuardContext";
import { useLoading } from "@contexts/LoadingContext";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import AppHeader from "./AppHeader";
import BoardCreationWizardOverlay from "@features/onboarding-wizard/BoardCreationWizardOverlay";
import SignInGate from "@features/auth/SignInGate/SignInGate";
import PastDueBanner from "@features/billing/PastDueBanner";
import { useFullLayoutAuthGateViewModel } from "./FullLayout.ViewModel";
import "./FullLayout.scss";

const FullLayout: React.FC = () => {
  const { t } = useTranslation();
  const {
    isAuthenticated,
    isLoading: authLoading,
    accessToken,
    regionBaseUrl,
    availableRegions,
  } = useAuth();
  const { isLoading: globalLoading, loadingMessage } = useLoading();
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    mainRef.current?.focus();
  }, [location.pathname]);

  const {
    shouldNavigateToRegionSelection,
    isRedirectingToLogin,
    loginRedirectFailed,
    requiresManualSignIn,
    popupBlocked,
    triggerLoginRedirect,
  } = useFullLayoutAuthGateViewModel({
    authLoading,
    globalLoading,
    isAuthenticated,
    accessToken,
    regionBaseUrl,
    availableRegions,
  });

  // Show loading screen if auth is initializing
  if (authLoading) {
    return <LoadingScreen message={t("common.labels.authenticating")} />;
  }

  // Show loading screen if global loading is active
  if (globalLoading) {
    return <LoadingScreen message={loadingMessage} />;
  }

  if (shouldNavigateToRegionSelection) {
    return <Navigate to="/regions" replace />;
  }

  if (!isAuthenticated && isRedirectingToLogin) {
    return <LoadingScreen message={t("common.labels.authenticating")} />;
  }

  // Not signed in: the redirect failed, the browser refused the popup, or we
  // are inside a host where sign-in has to start from the user's own click.
  if (!isAuthenticated) {
    return (
      <SignInGate
        // On the open web a redirect is already on its way, so the gate waits
        // rather than offering a button that would race it. Embedded hosts, a
        // refused pop-up and a failed attempt all get an actionable button.
        isSigningIn={
          isRedirectingToLogin ||
          (!requiresManualSignIn && !loginRedirectFailed && !popupBlocked)
        }
        hasFailed={loginRedirectFailed}
        popupBlocked={popupBlocked}
        onSignIn={() => void triggerLoginRedirect()}
      />
    );
  }

  return (
    <TourProvider>
      <MobileSidebarProvider>
        {/* Around both the header and the Outlet: the page registers the guard,
            the header's links are what ask it. */}
        <NavigationGuardProvider>
          <div className="full-layout__container">
            <AppHeader />
            <PastDueBanner />
            <main id="main-content" tabIndex={-1} ref={mainRef} className="full-layout__main">
              <Outlet />
            </main>
            <BoardCreationWizardOverlay />
          </div>
        </NavigationGuardProvider>
      </MobileSidebarProvider>
    </TourProvider>
  );
};

export default FullLayout;
